"use client";

import React, { useCallback, useEffect, useRef, useState, Component, type ReactNode, type ErrorInfo } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectItem } from '@/components/ui/select';
import { clientLogger } from "@/lib/client-logger";

class MapErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    clientLogger.error("Map rendering error caught by boundary", {
      error: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface LocationData {
  address: string;
  latitude: number;
  longitude: number;
  radiusMiles: number | null;
  hideAddress: boolean;
}

interface LocationMapPickerProps {
  initialLocation?: Partial<LocationData>;
  onLocationChange: (location: LocationData) => void;
  className?: string;
  /**
   * Optional "show map on this location page" toggle, rendered directly below
   * the address field. When provided and unchecked, the radius dropdown and map
   * preview are hidden. When omitted, both are always shown.
   */
  showMapControl?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    id: string;
  };
}

const RADIUS_OPTIONS = [
  { value: null, label: 'Exact Location (no radius)', hideAddress: false },
  { value: 0.25, label: '0.25 mile radius', hideAddress: true },
  { value: 0.5, label: '0.5 mile radius', hideAddress: true },
  { value: 1, label: '1 mile radius', hideAddress: true },
  { value: 5, label: '5 mile radius', hideAddress: true },
  { value: 10, label: '10 mile radius', hideAddress: true },
  { value: 25, label: '25 mile radius', hideAddress: true },
];

const MapComponent: React.FC<{
  center: google.maps.LatLngLiteral;
  zoom: number;
  onLocationSelect: (location: { lat: number; lng: number; address: string }) => void;
  radiusMiles: number | null;
}> = ({ center, zoom, onLocationSelect, radiusMiles }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<google.maps.Marker>();
  const [circle, setCircle] = useState<google.maps.Circle>();
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (ref.current && !map) {
      try {
        if (typeof google === 'undefined' || !google.maps) {
          setMapError(true);
          return;
        }
        const newMap = new window.google.maps.Map(ref.current, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        setMap(newMap);
        setGeocoder(new window.google.maps.Geocoder());
      } catch (err) {
        clientLogger.error("Error initializing location picker map", {
          error: err instanceof Error ? err.message : "Unknown error"
        });
        setMapError(true);
      }
    }
  }, [ref, map, center, zoom]);

  // Handle map clicks
  useEffect(() => {
    if (map && geocoder) {
      const clickListener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          
          // Reverse geocode to get address
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              onLocationSelect({
                lat,
                lng,
                address: results[0].formatted_address
              });
            }
          });
        }
      });

      return () => {
        google.maps.event.removeListener(clickListener);
      };
    }
  }, [map, geocoder, onLocationSelect]);

  // Update map center and zoom when location changes
  useEffect(() => {
    if (map) {
      map.setCenter(center);
      map.setZoom(zoom);
    }
  }, [map, center, zoom]);

  // Update marker position
  useEffect(() => {
    if (map) {
      if (marker) {
        marker.setMap(null);
      }
      
      const newMarker = new google.maps.Marker({
        position: center,
        map: map,
        draggable: true,
      });

      // Handle marker drag
      newMarker.addListener('dragend', () => {
        const position = newMarker.getPosition();
        if (position && geocoder) {
          const lat = position.lat();
          const lng = position.lng();
          
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              onLocationSelect({
                lat,
                lng,
                address: results[0].formatted_address
              });
            }
          });
        }
      });

      setMarker(newMarker);
    }
    // marker is used for cleanup but not as dependency to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, center, geocoder, onLocationSelect]);

  // Update radius circle
  useEffect(() => {
    if (map) {
      if (circle) {
        circle.setMap(null);
      }

      if (radiusMiles && radiusMiles > 0) {
        const newCircle = new google.maps.Circle({
          strokeColor: '#3B82F6',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#3B82F6',
          fillOpacity: 0.15,
          map: map,
          center: center,
          radius: radiusMiles * 1609.34, // Convert miles to meters
        });
        setCircle(newCircle);
      }
    }
    // circle is used for cleanup but not as dependency to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, center, radiusMiles]);

  if (mapError) {
    return (
      <div className="w-full h-96 rounded-lg border bg-gray-50 flex items-center justify-center">
        <div className="text-center p-4">
          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm text-gray-500 font-medium">Map unavailable</p>
          <p className="text-xs text-gray-400 mt-1">You can still enter coordinates manually.</p>
        </div>
      </div>
    );
  }

  return <div ref={ref} className="w-full h-96 rounded-lg border" />;
};

const LocationMapPicker: React.FC<LocationMapPickerProps> = ({
  initialLocation,
  onLocationChange,
  className,
  showMapControl
}) => {
  // When no toggle is provided, the radius dropdown and map preview are always
  // shown. When provided, they follow the checkbox state.
  const showMapSections = showMapControl ? showMapControl.checked : true;
  const [location, setLocation] = useState<LocationData>({
    address: initialLocation?.address || '',
    latitude: initialLocation?.latitude || 40.7128,
    longitude: initialLocation?.longitude || -74.0060,
    radiusMiles: initialLocation?.radiusMiles || null,
    hideAddress: initialLocation?.hideAddress || false,
  });

  const [isGeocoding, setIsGeocoding] = useState(false);

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  const handleLocationSelect = useCallback((newLocation: { lat: number; lng: number; address: string }) => {
    const updatedLocation = {
      ...location,
      latitude: newLocation.lat,
      longitude: newLocation.lng,
      address: newLocation.address,
    };
    setLocation(updatedLocation);
    onLocationChange(updatedLocation);
  }, [location, onLocationChange]);

  const handleAddressChange = useCallback((address: string) => {
    const updatedLocation = { ...location, address };
    setLocation(updatedLocation);
    onLocationChange(updatedLocation);
  }, [location, onLocationChange]);

  const handleRadiusChange = useCallback((radiusValue: string) => {
    const radiusMiles = radiusValue === '' ? null : parseFloat(radiusValue);
    const radiusOption = RADIUS_OPTIONS.find(opt =>
      (opt.value === null && radiusMiles === null) || opt.value === radiusMiles
    );

    const updatedLocation = {
      ...location,
      radiusMiles,
      hideAddress: radiusOption?.hideAddress || false,
    };
    setLocation(updatedLocation);
    onLocationChange(updatedLocation);
  }, [location, onLocationChange]);

  const handleAddressSearch = useCallback(async (address: string) => {
    if (!address.trim()) return;

    if (typeof google === 'undefined' || !google.maps?.Geocoder) {
      clientLogger.warn("Google Maps not available for address search");
      // Still save the address text even without geocoding
      handleAddressChange(address);
      return;
    }

    setIsGeocoding(true);
    try {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const result = results[0];
          const lat = result.geometry.location.lat();
          const lng = result.geometry.location.lng();

          handleLocationSelect({
            lat,
            lng,
            address: result.formatted_address
          });
        }
        setIsGeocoding(false);
      });
    } catch (error) {
      clientLogger.error("Geocoding error", { error });
      setIsGeocoding(false);
    }
  }, [handleLocationSelect, handleAddressChange]);

  return (
    <div className={className}>
      <div className="space-y-4">
        <div>
          <Label htmlFor="address">Location Address</Label>
          <div className="flex gap-2 mt-1">
            <input
              id="address"
              type="text"
              value={location.address}
              onChange={(e) => setLocation(prev => ({ ...prev, address: e.target.value }))}
              onBlur={(e) => handleAddressChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddressSearch(location.address);
                }
              }}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Enter address"
            />
            {apiKey && (
              <Button
                type="button"
                onClick={() => handleAddressSearch(location.address)}
                disabled={isGeocoding}
                variant="outline"
              >
                {isGeocoding ? 'Searching...' : 'Search'}
              </Button>
            )}
          </div>
        </div>

        {showMapControl && (
          <div>
            <div className="flex items-center space-x-2">
              <input
                id={showMapControl.id}
                type="checkbox"
                checked={showMapControl.checked}
                onChange={(e) => showMapControl.onChange(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <Label htmlFor={showMapControl.id}>Show map on this location page</Label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              When enabled, participants visiting this location&apos;s page will see a map with its configured radius.
            </p>
          </div>
        )}

        {showMapSections && (
          <div>
            <Label htmlFor="radius">Location Radius</Label>
            <Select
              id="radius"
              value={location.radiusMiles?.toString() || ''}
              onChange={(e) => handleRadiusChange(e.target.value)}
              className="mt-1"
            >
              {RADIUS_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value?.toString() || 'exact'}
                  value={option.value?.toString() || ''}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {location.radiusMiles
                ? `Address will be hidden from participants. They'll see a ${location.radiusMiles} mile radius area.`
                : 'Exact address will be shown to participants.'
              }
            </p>
          </div>
        )}

        {showMapSections && (apiKey ? (
          <MapErrorBoundary
            fallback={
              <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border">
                <div className="text-center p-4">
                  <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm text-gray-500 font-medium">Map unavailable</p>
                  <p className="text-xs text-gray-400 mt-1">You can still enter an address manually above.</p>
                </div>
              </div>
            }
          >
            <Wrapper
              apiKey={apiKey}
              libraries={['geometry', 'places']}
              render={(status) => {
                if (status === 'LOADING') {
                  return (
                    <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg border">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="text-gray-600">Loading map...</p>
                      </div>
                    </div>
                  );
                }

                if (status === 'FAILURE') {
                  return (
                    <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border">
                      <div className="text-center p-4">
                        <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <p className="text-sm text-gray-500 font-medium">Map unavailable</p>
                        <p className="text-xs text-gray-400 mt-1">You can still enter an address manually above.</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <MapComponent
                    center={{ lat: location.latitude, lng: location.longitude }}
                    zoom={location.radiusMiles ? Math.max(16 - Math.log2(location.radiusMiles * 4), 10) : 15}
                    onLocationSelect={handleLocationSelect}
                    radiusMiles={location.radiusMiles}
                  />
                );
              }}
            />
          </MapErrorBoundary>
        ) : (
          <p className="text-xs text-gray-400">Map preview unavailable. Enter the address manually above.</p>
        ))}
      </div>
    </div>
  );
};

export default LocationMapPicker;
